/* eslint sonarjs/cognitive-complexity: 1 */

import type {
  CentAmount,
  Product,
  ProductImage,
  Variant,
} from '@scayle/storefront-nuxt'
import { useRxFittingHeight } from './useRxFittingHeight'
import { useRxPrescription } from './useRxPrescription'
import { useFimProduct } from './useFimProduct'
import { useAppointments } from './useAppointments'
import {
  tintingOptionsFallback,
  tintingOptionsFilterMap,
  rxLensQualityHexCodes,
  rxLensQualityColors,
  LensTypesRefKeys,
} from '~/constants/rx'
import { RxConfMasterKeys } from '~/rpcMethods/rxTypes'
import { useFimVariant } from '~/composables/useFimVariant'

export type ConfiguratorStateValue = {
  product: Product
  variant: Variant
}

export type ConfiguratorState = {
  frame?: ConfiguratorStateValue
  lensType?: ConfiguratorStateValue
  lensQuality?: ConfiguratorStateValue
  tintingType?: ConfiguratorStateValue
  prescriptionValues?: IPrescriptionValues
  freeService?: ConfiguratorStateValue
}

type ConfiguratorSeralizedItem = {
  variantId: number
  quantity: number
  itemGroup: {
    id: string
    isMainItem: boolean
    isRequired: boolean
  }
  promotionId?: string
}

export type RxSubItemInfo = {
  name: string
  priceWithTax: number
}

export type ConfiguratorSerializedMainCustomData = FimBasketItemCustomData & {
  itemGroup: {
    id: string
    isMainItem: boolean
    isRequired: boolean
  }
  prescriptionValues?: RxPrescriptionValues
  shopId: number
  rxSubItemInfo: ObjectWith<RxSubItemInfo>[]
  prescriptionValuesDisplay?: RxPrescriptionDisplayValues
}

export type ConfiguratorSerializedAddonCustomData = {
  hideSubItem: true
} & Omit<
  ConfiguratorSerializedMainCustomData,
  'prescriptionValues' | 'rxSubItemInfo'
>

export type ConfiguratorSerializedMain = ConfiguratorSeralizedItem & {
  customData: ConfiguratorSerializedMainCustomData
  displayData?: RxBasketItemDisplayData
}

export type ConfiguratorSerializedAddon = ConfiguratorSeralizedItem & {
  customData: MakeOptional<ConfiguratorSerializedAddonCustomData, 'itemGroup'>
}

interface SingleOptionProduct {
  title: string
  icon?: string
  description: string
  price: CentAmount
  productId: number
  variantId: number
  referenceKey: string
  sortingKey: number
}

interface SingleOptionAppointment {
  title: string
  icon: string
  description: string
  referenceKey: string
  sortingKey: number
}

export type SingleOption = SingleOptionProduct | SingleOptionAppointment

export type LensOption = SingleOptionProduct & {
  image: ProductImage
  infoText: string
  baseUsp: { icon: string; label: string }[]
  onTopUsp: { icon: string; label: string }[]
  isRecommendation: boolean
  isInclusive: boolean
  color: string
}

export type ColorOptionValue = {
  variantId: number
  color: string
  name: string
  selected: boolean
  referenceKey: string
  sortingKey: number
}

export type ColorOption = {
  title: string
  description: string
  price: CentAmount
  productId: number
  referenceKey: string
  values: ColorOptionValue[]
  sortingKey: number
}

type ConfiguratorOptionSetter = (variantId: Variant['id']) => ConfiguratorState

export type ConfiguratorSerialized = Array<
  ConfiguratorSerializedMain | ConfiguratorSerializedAddon
>

type RxConfigurationStateComposable = {
  init: (product: Product, variant: Variant) => Promise<void>
  reset: () => void
  fetching: Ref<boolean>
  state: ComputedRef<ConfiguratorState>
  lensTypes: ComputedRef<SingleOption[]>
  setLensType: (variantId: number) => void
  lensQualities: ComputedRef<LensOption[]>
  setLensQuality: (variantId: number) => void
  tintingTypes: ComputedRef<ColorOption[]>
  setTintingType: (variantId: number) => void
  getPrescriptionPropSetters: (prescriptionProp: IPrescriptionProp) => {
    setLeft: (left: number) => void
    setRight: (right: number) => void
  }
  setPrescriptionValues: (values: IPrescriptionValues) => void
  serialize: (editingGroupId?: string) => Promise<ConfiguratorSerialized>
  deserialize: (data: ConfiguratorSerialized) => Promise<void>
  resetPrescriptionValues: () => void
  resetLensType: () => void
  resetLensQuality: () => void
  resetTintingType: () => void
  isValid: () => boolean
  isPrescriptionValid: ComputedRef<boolean>
  supportsPrescriptionValues: ComputedRef<boolean>
  fetchFittingHeight: () => void
  isError: ComputedRef<boolean>
  setErrorState: (state: boolean) => void
  appointmentUrl: ComputedRef<string>
  isHomeTryOn: Ref<boolean>
  setIsHomeTryOn: (state: boolean) => void
}

const state = reactive<ConfiguratorState>({
  frame: undefined,
  lensType: undefined,
  lensQuality: undefined,
  tintingType: undefined,
  prescriptionValues: undefined,
  freeService: undefined,
})

const isHomeTryOn = ref(false)
const setIsHomeTryOn = (value: boolean) => {
  isHomeTryOn.value = value
  if (value) {
    state.lensType = undefined
  }
}

const isError = ref(false)
const setErrorState = (state: boolean) => {
  isError.value = state
}

export type RxMainAction =
  | 'continue'
  | 'appointment'
  | 'check' // Online Sehcheck
  | 'addToBasket'
  | 'skipValues'

/**
 * RX Configurator state
 *
 * @usage example
 *
    const {
      init, // Initializes configurator state & fetches products (glasses, etc...)
      state, // Current state
      lensTypes, // SingleOptionProduct | SingleOptionAppointment
      setLensType, // ConfiguratorOptionSetter
      lensQualities, // LensOption
      setLensQuality, // ConfiguratorOptionSetter
      tintingTypes, // ColorOption
      setTintingType, // ConfiguratorOptionSetter
      serialize,
      deserialize,
    } = await useRxConfiguratorState()

 */

export const useRxConfiguratorState = () => {
  const instance = useNuxtApp()
  const { $i18n, $currentShop } = instance

  const {
    data: fetchedVariant,
    fetching: fetchingVariant,
    fetch: fetchVariant,
  } = useFimVariant()

  const {
    data: fetchedProduct,
    fetching: fetchingProduct,
    fetch: fetchProduct,
  } = useFimProduct()

  const {
    data: rxFittingHeight,
    fetch: rpcFetchRxFittingHeight,
    setData: setRxFittingHeight,
    waitForData: rxFittingHeightPending,
  } = useRxFittingHeight()

  const {
    prescriptionValues,
    setPrescriptionValues,
    getPrescriptionPropSetters,
    hasValidPrescriptionValues,
    resetPrescriptionValues,
    isPrescriptionValid,
  } = useRxPrescription()

  const rxProductsPromise = useRxProducts()

  const {
    fetch,
    fetching,
    rxLensQualityIndex,
    rxLensTypes,
    rxTintingTypes,
    rxFreeServiceProduct,
  } = rxProductsPromise

  const { getAppointmentUrl } = useAppointments({ lazy: true })

  const combinedState = computed<ConfiguratorState>(() => ({
    ...state,
    prescriptionValues: prescriptionValues.value,
  }))

  const init = async (product: Product, variant: Variant) => {
    await fetch()
    state.frame = { product, variant }
    state.lensQuality = undefined
    state.tintingType = undefined

    const freeFitting = rxFreeServiceProduct.value.find(
      ({ referenceKey }) => referenceKey === 'service_fitting',
    )
    if (freeFitting?.variants?.[0]) {
      state.freeService = {
        product: freeFitting,
        variant: freeFitting.variants[0],
      }
    }
  }

  const isHomeTryOnEnabled = computed(() => {
    return (
      // Enabled in store config
      !!$currentShop?.rx?.homeTryOn &&
      // We need a product
      state.frame?.product &&
      // Products with 'nulltarif' attribute are not eligible for home try on
      isHomeTryOnEligible(state.frame?.product, $currentShop) &&
      // Product price should not be 0
      getLowestPrice(state.frame?.product)?.withTax !== 0
    )
  })

  /* Step 1 - lens type */
  const lensTypes = computed<SingleOption[]>(() =>
    [
      ...(rxLensTypes?.value?.map((item) => ({
        title: getFirstAttributeLabel(item.attributes, 'name') || '',
        icon: 'IconFielmannGlasses',
        description:
          parseMarkdownAttribute(
            getAdvancedAttribute({
              product: item,
              property: 'shortDescriptionFormatted',
            }),
          ) || '',
        price: getLowestPrice(item)?.withTax,
        productId: item.id,
        variantId: item?.variants?.[0]?.id,
        referenceKey: item.referenceKey || '',
        sortingKey: getRxSortingValue(item), // products without sorting key get added at the end
      })) ?? []),

      isHomeTryOnEnabled.value
        ? {
            referenceKey: LensTypesRefKeys.HOME_TRY_ON,
            icon: 'IconFielmannBox',
            title: $i18n.t('rx.home_try_on.title'),
            description: $i18n.t('rx.home_try_on.description'),
            sortingKey: Number.POSITIVE_INFINITY,
          }
        : undefined,
      {
        referenceKey: LensTypesRefKeys.APPOINTMENT,
        icon: 'IconFielmannStore',
        title: $i18n.t('rx.varifocals.title'),
        description: $i18n.t('rx.varifocals.description'),
        sortingKey: Number.POSITIVE_INFINITY,
      },
    ]
      .filter(Boolean)
      .sort((a, b) => a.sortingKey - b.sortingKey),
  )

  const setLensType: ConfiguratorOptionSetter = (variantId: number) => {
    const product = rxLensTypes.value.find((item) =>
      item.variants?.some((variant) => variant.id === variantId),
    )

    const variant = product?.variants?.find(
      (variant) => variant.id === variantId,
    )

    if (product && variant) {
      state.lensType = { product, variant }
      return combinedState.value
    }

    throw new Error(
      `Could not find rx_lens_types variant with id: ${variantId}`,
    )
  }

  const rxIndexAvailability = computed<string[]>(() =>
    getRxCountryIndexAvailability(state?.frame?.product, $currentShop),
  )

  /* Step 3 - lens quality */
  const lensQualities = computed<LensOption[]>(() => {
    const availableQualities =
      rxLensQualityIndex?.value?.filter((item) =>
        rxIndexAvailability?.value?.includes(item?.referenceKey ?? ''),
      ) ?? []

    const cheapestLensQuality = getCheapestGlassProduct(availableQualities)

    const isInclusive = (item: Product): boolean => {
      return cheapestLensQuality?.referenceKey === item.referenceKey
    }

    const recommendationRef = getLensQualityRecommendationRef(
      availableQualities,
      prescriptionValues.value,
    )

    const getQualityColor = (
      item: Product,
      isRecommendation: boolean,
    ): string => {
      if (isRecommendation) {
        return rxLensQualityHexCodes.RECOMMENDED
      }

      const color =
        getFirstAttributeLabel(
          item.attributes,
          'rxIndexHighlightHexColorCode',
        ) || ''
      return (
        color ||
        (item.referenceKey && rxLensQualityColors[item.referenceKey]
          ? rxLensQualityColors[item.referenceKey]
          : rxLensQualityHexCodes.STANDARD_PLUS)
      )
    }

    return availableQualities
      .map<LensOption>((item) => {
        const isRecommendation =
          supportsPrescriptionValues.value &&
          Boolean(recommendationRef) &&
          item.referenceKey === recommendationRef

        return {
          title: getFirstAttributeLabel(item.attributes, 'name') || '',
          description: isRecommendation
            ? $i18n.t('rx.recommendation.info_text')
            : '',
          price: getLowestPrice(item)?.withTax,
          image: item?.images?.[0],
          infoText:
            parseMarkdownAttribute(
              getAdvancedAttribute({
                product: item,
                property: 'shortDescriptionFormatted',
              }),
            ) || '',
          baseUsp: getLensQualityUsp(item, 'rxBaseUsp') ?? [],
          onTopUsp: getLensQualityUsp(item, 'rxOnTopUsp') ?? [],
          isRecommendation,
          productId: item.id,
          variantId: item.variants?.[0].id ?? 0,
          referenceKey: item.referenceKey || '',
          sortingKey: getRxSortingValue(item),
          isInclusive: isInclusive(item),
          color: getQualityColor(item, isRecommendation),
        }
      })
      .sort((a, b) => a.sortingKey - b.sortingKey)
  })

  const setLensQuality: ConfiguratorOptionSetter = (variantId: number) => {
    const product = rxLensQualityIndex.value.find((item) =>
      item.variants?.some((variant) => variant.id === variantId),
    )

    const variant = product?.variants?.find(
      (variant) => variant.id === variantId,
    )

    if (product && variant) {
      state.lensQuality = { product, variant }
      return combinedState.value
    }

    throw new Error(
      `Could not find rx_lens_quality_index variant with id: ${variantId}`,
    )
  }

  /* Step 4 - tinting types / extras */
  const tintingTypes = computed<ColorOption[]>(() => {
    let productTintingTypes = rxTintingTypes.value.filter(
      (item) =>
        state?.lensQuality?.product?.referenceKey &&
        tintingOptionsFilterMap[
          state?.lensQuality?.product?.referenceKey
        ].includes(item.referenceKey || ''),
    )

    // Add the old tinting types as fallback in case the new products are missing
    if (
      productTintingTypes.length === 0 &&
      state?.lensQuality?.product?.referenceKey
    ) {
      productTintingTypes = rxTintingTypes.value.filter((item) =>
        tintingOptionsFallback.includes(item.referenceKey || ''),
      )
    }

    return productTintingTypes
      .map<ColorOption>((item) => {
        return {
          title: getFirstAttributeLabel(item.attributes, 'name') || '',
          description:
            parseMarkdownAttribute(
              getAdvancedAttribute({
                product: item,
                property: 'shortDescriptionFormatted',
              }),
            ) || '',
          price: getLowestPrice(item)?.withTax,
          productId: item.id,
          referenceKey: item.referenceKey || '',
          sortingKey: getRxSortingValue(item),
          values: (
            item.variants?.map<ColorOptionValue>((variant) => ({
              referenceKey: variant.referenceKey || '',
              variantId: variant.id,
              color: variant?.attributes?.rxTintingHexColorCode
                ? `#${getAttributeValue(
                    variant.attributes,
                    'rxTintingHexColorCode',
                  )}`
                : '',
              selected: false,
              name: getFirstAttributeLabel(
                variant.attributes,
                'rxTintingColorDescription',
              ),
              sortingKey: getRxSortingValue(variant),
            })) ?? []
          ).sort((a, b) => a.sortingKey - b.sortingKey),
        }
      })
      .sort((a, b) => {
        return a.sortingKey - b.sortingKey
      })
  })

  const setTintingType: ConfiguratorOptionSetter = (variantId: number) => {
    const product = rxTintingTypes.value.find((item) =>
      item.variants?.some((variant) => variant.id === variantId),
    )

    const variant = product?.variants?.find(
      (variant) => variant.id === variantId,
    )

    if (product && variant) {
      state.tintingType = { product, variant }

      return combinedState.value
    }

    throw new Error(
      `Could not find rx_tinting_types variant with id: ${variantId}`,
    )
  }

  const supportsPrescriptionValues = computed(
    () => state.lensType?.variant.referenceKey !== 'type_none',
  )

  const isValid = () =>
    !!state.frame?.variant.id &&
    !!state.lensType?.variant.id &&
    !!state.lensQuality?.variant.id &&
    !!state.tintingType?.variant.id

  const fetchFittingHeight = () => {
    if (
      state.frame?.variant.referenceKey &&
      prescriptionValues.value.pd.left &&
      prescriptionValues.value.pd.right
    ) {
      rpcFetchRxFittingHeight({
        referenceKey: state.frame.variant.referenceKey,
        pd: prescriptionValues.value.pd as Required<IPrescriptionValue>,
      })
    }
  }

  const serialize = async (
    editingGroupId?: string,
  ): Promise<ConfiguratorSerialized> => {
    if (!isValid()) {
      const missing = [
        !state.frame?.variant?.id && 'frame',
        !state.lensType?.variant?.id && 'lensType',
        !state.lensQuality?.variant?.id && 'lensQuality',
        !state.tintingType?.variant?.id && 'tintingType',
      ].filter(Boolean)

      const errorHint = missing.length
        ? `Missing ${missing.join(', ')}`
        : 'Invalid prescription values'
      throw new Error(`All values need to be set to serialize. ${errorHint}`)
    }
    const validState = state as Required<ConfiguratorState>

    const groupId = editingGroupId || generateRandomBytes()
    const colors = getColors(validState.frame.product)
    const frameSizes = getFrameSizesForVariant(validState.frame.variant)
    let prescriptionValuesSerialized: RxPrescriptionValues | undefined

    const mainItemSerialized: ConfiguratorSerializedMain = {
      ...getMainSerializedItem(
        groupId,
        validState.frame.variant.id,
        $currentShop.shopId,
        [
          validState.lensType,
          validState.lensQuality,
          validState.tintingType,
          validState.freeService,
        ],
      ),
    }

    const branchId = getBranchIdFromSession()

    mainItemSerialized.customData = {
      ...mainItemSerialized.customData,
      ...(branchId && { branchId }),
      designerOrder: isDesignerProduct(validState.frame.product),
      frameOrder: false,
    }

    if (supportsPrescriptionValues.value && hasValidPrescriptionValues.value) {
      prescriptionValuesSerialized = await getPrescriptionValuesSerialized(
        rxFittingHeightPending,
        prescriptionValues.value,
        rxFittingHeight,
      )
      mainItemSerialized.customData.prescriptionValues =
        prescriptionValuesSerialized
      mainItemSerialized.customData.prescriptionValuesDisplay =
        formatSightSpecificationForDisplay(
          prescriptionValuesSerialized,
          $currentShop,
          false,
        )
    }

    mainItemSerialized.displayData = getRxDisplayData(
      $i18n,
      $currentShop,
      colors,
      frameSizes,
      prescriptionValuesSerialized,
    )

    const addOnItemsSerialized = getAddonSerializedItems(
      groupId,
      [
        validState.lensType,
        validState.lensQuality,
        validState.tintingType,
        validState.freeService,
      ],
      $currentShop.shopId,
    )

    return [mainItemSerialized, ...addOnItemsSerialized]
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const deserialize = async (data: ConfiguratorSerialized) => {
    // reset state
    reset()

    const mainItem = getItemGroupMainItem(
      data,
      getItemGroupId(data[0]),
    ) as ConfiguratorSerializedMain

    if (
      !state.frame?.product ||
      !state.frame.variant ||
      state.frame.variant.id !== mainItem.variantId
    ) {
      await fetchVariant({
        ids: [mainItem.variantId],
      })

      if (!fetchedVariant.value?.length) {
        throw new Error(`Variant ${mainItem.variantId} not found`)
      }
      const variant = fetchedVariant.value[0]

      await fetchProduct({
        id: variant.productId,
        includeSellableForFree: true,
        with: {
          attributes: 'all',
          advancedAttributes: 'all',
          variants: 'all',
          images: 'all',
        },
      })
      if (!fetchedProduct.value) {
        throw new Error(`Product ${variant.productId} not found`)
      }

      const product = fetchedProduct.value
      state.frame = { product, variant }
    }

    // make sure we have everything
    if (
      !rxLensQualityIndex.value.length ||
      !rxLensTypes.value.length ||
      !rxTintingTypes.value.length ||
      !rxFreeServiceProduct.value.length
    ) {
      await fetch()
    }

    if (mainItem) {
      const values = mainItem.customData?.prescriptionValues
      if (values) {
        if (values.left.hpd && values.right.hpd) {
          setRxFittingHeight({
            hpd: {
              left: +values.left.hpd,
              right: +values.right.hpd,
            },
          })
        }
        setPrescriptionValues({
          sphere: {
            left: +values.left.sphere,
            right: +values.right.sphere,
          },
          cylinder: {
            left: +values.left.cylinder,
            right: +values.right.cylinder,
          },
          axis: {
            left: +values.left.axis,
            right: +values.right.axis,
          },
          pd: {
            left: +values.left.pd,
            right: +values.right.pd,
          },
        })
      }
    }

    const addonProducts = [
      ...rxTintingTypes.value,
      ...rxLensQualityIndex.value,
      ...rxLensTypes.value,
      ...rxFreeServiceProduct.value,
    ]

    const addonItems = data.filter((item) => isRxAddonItem(item))
    for (const item of addonItems) {
      const product = addonProducts.find((product) =>
        product.variants?.some((variant) => variant.id === item.variantId),
      )

      const variant = product?.variants?.find(
        (variant) => variant.id === item.variantId,
      )

      if (product && variant) {
        const value = { product, variant }
        switch (product?.masterKey) {
          case RxConfMasterKeys.tintingTypes:
            state.tintingType = value
            break
          case RxConfMasterKeys.lensQuality:
            state.lensQuality = value
            break
          case RxConfMasterKeys.lensTypes:
            state.lensType = value
            break
          case RxConfMasterKeys.freeService:
            state.freeService = value
            break
        }
      }
    }
  }

  const reset = () => {
    state.frame = undefined
    state.lensType = undefined
    state.lensQuality = undefined
    state.tintingType = undefined
  }

  const appointmentUrl = computed(() => getAppointmentUrl())

  return extendPromise(
    rxProductsPromise.then(() => ({})),
    {
      init,
      reset,
      appointmentUrl,
      fetching: fetching || fetchingVariant || fetchingProduct,
      state: combinedState,
      lensTypes,
      setLensType,
      lensQualities,
      setLensQuality,
      tintingTypes,
      setTintingType,
      getPrescriptionPropSetters,
      setPrescriptionValues,
      serialize,
      deserialize,
      resetPrescriptionValues,
      resetLensType: () => {
        state.lensType = undefined
      },
      resetLensQuality: () => {
        state.lensQuality = undefined
      },
      resetTintingType: () => {
        state.tintingType = undefined
      },
      isPrescriptionValid: computed(() =>
        isPrescriptionValid(supportsPrescriptionValues.value),
      ),
      supportsPrescriptionValues,
      isValid,
      fetchFittingHeight,
      isError: computed(() => isError.value),
      setErrorState,
      isHomeTryOn,
      setIsHomeTryOn,
    },
  )
}
